import { useEffect, useState, useRef, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import type { SmithingRecipe } from '../../../server/src/skills/SmithingSkillSystem';

export interface SmithingState {
  level: number;
  xp: number;
  nextLevelXp: number;
  isSmithing: boolean;
  currentRecipe: SmithingRecipe | null;
  itemsSmithed: number;
  batchSize: number;
  nextSmithTime: number | undefined;
}

export interface UseSmithingReturn {
  smithingState: SmithingState;
  recipes: SmithingRecipe[];
  startSmithing: (recipeId: string, quantity: number) => void;
  stopSmithing: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to manage smithing skill progression and active smithing sessions
 */
export const useSmithing = (socketRef: React.MutableRefObject<Socket | null>): UseSmithingReturn => {
  const [smithingState, setSmithingState] = useState<SmithingState>({
    level: 1,
    xp: 0,
    nextLevelXp: 0,
    isSmithing: false,
    currentRecipe: null,
    itemsSmithed: 0,
    batchSize: 0,
    nextSmithTime: undefined,
  });

  const [recipes, setRecipes] = useState<SmithingRecipe[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

   // Fetch available recipes and player smithing stats
   const fetchSmithingData = useCallback(() => {
     if (!socketRef.current || !socketRef.current.connected) {
       console.log('❌ Socket not connected, skipping recipe fetch');
       return;
     }

     console.log('🔄 Fetching smithing data...');
     setIsLoading(true);
     
     // Set a timeout to stop loading if no response
     const timeout = setTimeout(() => {
       console.warn('⚠️ Smithing data fetch timeout - no response from server');
       setIsLoading(false);
     }, 3000);
     
     // Request recipes
     socketRef.current.emit('smithing:getRecipes', {});
     
     // Request stats
     socketRef.current.emit('smithing:getStats', {});

     return () => clearTimeout(timeout);
   }, [socketRef]);

  // Update smithing progress regularly
  const startProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(() => {
      if (!socketRef.current) return;
      socketRef.current.emit('smithing:getProgress', {});
    }, 500); // Update every 500ms
  }, [socketRef]);

  const stopProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Set up socket event listeners
  useEffect(() => {
    if (!socketRef.current) return;

    // Receive recipes
    const handleRecipes = (data: { recipes: SmithingRecipe[]; playerLevel: number }) => {
      console.log(`⚒️ Received smithing recipes - Count: ${data.recipes?.length || 0}, Level: ${data.playerLevel}`);
      if (data.recipes && data.recipes.length > 0) {
        console.log(`   First recipe: ${data.recipes[0].name} (Level ${data.recipes[0].requiredLevel})`);
      }
      setRecipes(data.recipes);
      setIsLoading(false);
    };

    // Receive stats
    const handleStats = (data: {
      level: number;
      totalXp: number;
      currentXp: number;
      nextLevelXp: number;
      isSmithing: boolean;
      currentRecipe: SmithingRecipe | null;
      itemsSmithed: number;
      batchSize: number;
      nextSmithTime: number | undefined;
    }) => {
      setSmithingState({
        level: data.level,
        xp: data.currentXp,
        nextLevelXp: data.nextLevelXp,
        isSmithing: data.isSmithing,
        currentRecipe: data.currentRecipe,
        itemsSmithed: data.itemsSmithed,
        batchSize: data.batchSize,
        nextSmithTime: data.nextSmithTime,
      });
      setIsLoading(false);
    };

    // Receive progress updates
    const handleProgress = (data: {
      isSmithing: boolean;
      currentRecipe: SmithingRecipe | null;
      itemsSmithed: number;
      batchSize: number;
      nextSmithTime: number | undefined;
      level?: number;
      currentXp?: number;
      nextLevelXp?: number;
    }) => {
      setSmithingState(prev => ({
        ...prev,
        level: data.level ?? prev.level,
        xp: data.currentXp ?? prev.xp,
        nextLevelXp: data.nextLevelXp ?? prev.nextLevelXp,
        isSmithing: data.isSmithing,
        currentRecipe: data.currentRecipe,
        itemsSmithed: data.itemsSmithed,
        batchSize: data.batchSize,
        nextSmithTime: data.nextSmithTime,
      }));
    };

    // Handle smithing started
    const handleStarted = (data: { recipeId: string; quantity: number }) => {
      startProgressUpdates();
    };

    // Handle smithing stopped
    const handleStopped = (data: { smithedCount: number; failedCount: number }) => {
      stopProgressUpdates();
      setSmithingState(prev => ({
        ...prev,
        isSmithing: false,
        currentRecipe: null,
        itemsSmithed: 0,
        batchSize: 0,
      }));
    };

    // Handle errors
    const handleError = (data: { message: string }) => {
      setError(data.message);
      stopProgressUpdates();
    };

    socketRef.current.on('smithing:recipes', handleRecipes);
    socketRef.current.on('smithing:stats', handleStats);
    socketRef.current.on('smithing:progress', handleProgress);
    socketRef.current.on('smithing:started', handleStarted);
    socketRef.current.on('smithing:stopped', handleStopped);
    socketRef.current.on('smithing:error', handleError);

    const socket = socketRef.current;
    return () => {
      if (!socket) return;
      socket.off('smithing:recipes', handleRecipes);
      socket.off('smithing:stats', handleStats);
      socket.off('smithing:progress', handleProgress);
      socket.off('smithing:started', handleStarted);
      socket.off('smithing:stopped', handleStopped);
      socket.off('smithing:error', handleError);
      stopProgressUpdates();
    };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [startProgressUpdates, stopProgressUpdates]);

     // Fetch initial data on mount and whenever socket becomes available
     // eslint-disable-next-line react-hooks/exhaustive-deps
     useEffect(() => {
       const attemptFetch = () => {
         if (!socketRef.current?.connected) {
           // Socket not ready, try again soon
           console.log('⏳ Socket not connected, retrying in 100ms...');
           const timer = setTimeout(attemptFetch, 100);
           return () => clearTimeout(timer);
         }
         
         console.log('✅ Socket connected, fetching smithing data');
         fetchSmithingData();
       };

       return attemptFetch();
      }, [fetchSmithingData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Start smithing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const startSmithing = useCallback(
      (recipeId: string, quantity: number) => {
        if (!socketRef.current) return;
        setError(null);
        socketRef.current.emit('smithing:start', { recipeId, quantity });
      },
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Stop smithing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stopSmithing = useCallback(() => {
      if (!socketRef.current) return;
      socketRef.current.emit('smithing:stop', {});
    }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );

  return {
    smithingState,
    recipes,
    startSmithing,
    stopSmithing,
    isLoading,
    error,
  };
};
